
import { Vue, Component } from "vue-property-decorator";
import { v4 as uuid } from "uuid";
import storage from "@/services/storage";
import splitFileNameExt from "@/utils/splitFileNameExt";

import ImageUploadIcon from "@/components/icons/ImageUploadIcon.vue";

import { NOTIFICATION } from "@/entities/Events";
import Message from "@/data/notificationsMessages";
import { FileSizeRestrictions } from "@/entities/FileSizeRestrictions";

interface HTMLInputEvent extends Event {
  target: HTMLInputElement & EventTarget;
}

@Component({
  components: {
    ImageUploadIcon,
  },
})
export default class FormImageUpload extends Vue {
  chooseFiles(): void {
    (this.$refs.fileUpload as HTMLInputElement).click();
  }

  checkFileSize(file: File): boolean {
    const filesize = parseFloat((file.size / 1024 / 1024).toFixed(4)); // MB
    return filesize <= FileSizeRestrictions.WORK_SAMPLE_MAX;
  }

  checkFileType(file: File): boolean {
    return (
      file.type === "image/jpeg" ||
      file.type === "image/gif" ||
      file.type === "image/png"
    );
  }

  validateFile(file: File): { valid: boolean, message: string }  {
    if (!this.checkFileType(file)) {
      return {
        valid: false,
        message: "Invalid file type",
      };
    }

    if (!this.checkFileSize(file)) {
      return {
        valid: false,
        message: "File is too big",
      };
    }

    return {
      valid: true,
      message: "File is valid",
    };
  }

  addFiles(event: DragEvent): void {
    if (!event.dataTransfer) {
      return;
    }

    this.uploadFile(event.dataTransfer.files[0]);
  }

  changeFiles(event: HTMLInputEvent): void {
    if (!event.target.files) {
      return;
    }

    this.uploadFile(event.target.files[0]);
  }

  async uploadFile(file: File): Promise<void> {
    const { valid, message = "" } = this.validateFile(file);
    
    if (!valid) {
      this.$eventBus.emit(
        NOTIFICATION, 
        Message.errorMessage(message)
      );

      return;
    }

    try {
      const { ext, name } = splitFileNameExt(file.name);
      const s3Filename = `${name}-${uuid()}.${ext}`;

      await storage.save(s3Filename, file);

      this.$emit("update", s3Filename);
    } catch (_) { }
  }
}
